import { useEffect, useRef } from 'react';
import { useDialogSessionOptional } from '@/features/DialogSession';

type Params = {
  /** Переписка и данные о диалоге — на разных панелях, видна одна из них */
  isEnabled: boolean;
  /** Сейчас показана панель переписки */
  isTranscriptShown: boolean;
  /** Переключиться на панель переписки */
  showTranscript: () => void;
};

/**
 * Переход к месту переписки с панели данных о диалоге — например, из вкладки «Вложения» — сначала
 * показывает панель переписки, а прокручивает её, когда переписка появилась: в скрытой панели её нет.
 */
export const useTranscriptPanelReveal = ({ isEnabled, isTranscriptShown, showTranscript }: Params): void => {
  const registerShowTranscript = useDialogSessionOptional()?.registerShowTranscript;
  // Переход, ждущий показа панели переписки
  const pendingJumpRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isEnabled || !registerShowTranscript) return;
    return registerShowTranscript((jump) => {
      if (isTranscriptShown) {
        jump();
        return;
      }
      pendingJumpRef.current = jump;
      showTranscript();
    });
  }, [isEnabled, registerShowTranscript, isTranscriptShown, showTranscript]);

  useEffect(() => {
    if (!isTranscriptShown || !pendingJumpRef.current) return;
    // Следующим кадром: только что показанная переписка ещё раскладывается.
    // Переход снимается в самом кадре — повторный запуск эффекта его не теряет
    const frameId = requestAnimationFrame(() => {
      pendingJumpRef.current?.();
      pendingJumpRef.current = null;
    });
    return () => cancelAnimationFrame(frameId);
  }, [isTranscriptShown]);
};
